import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { api } from '@/lib/api';
import { APP_VERSION } from '@/version';
import {
    Home,
    ShoppingBag,
    FileText,
    Bell,
    MessageCircle,
    Users,
    LogOut,
    Menu,
    X,
    Skull,
    DollarSign,
    Target,
    Webhook,
    Puzzle,
    Code,
    ChevronDown,
    Activity,
    Search,
    Layers,
    Bug,
    ExternalLink,
    Compass,
    BookOpen,
    Crown,
    Settings,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';


const baseNavItems = [
    { to: '/dashboard', icon: Home, label: 'Home' },
    { to: '/marketplace', icon: ShoppingBag, label: 'Marketplace' },
    { to: '/discover', icon: Compass, label: 'Discover' },
    { to: '/wall-of-shame', icon: Skull, label: 'Shame' },
    { to: '/feed', icon: DollarSign, label: 'Feed' },
    { to: '/pillory', icon: Target, label: 'Pillory' },
];

const domNavItems = [
    { to: '/dashboard', icon: Home, label: 'Home' },
    { to: '/my-subs', icon: Users, label: 'My Subs' },
    { to: '/my-templates', icon: BookOpen, label: 'Templates' },
    { to: '/marketplace', icon: ShoppingBag, label: 'Marketplace' },
    { to: '/discover', icon: Compass, label: 'Discover' },
    { to: '/wall-of-shame', icon: Skull, label: 'Shame' },
    { to: '/feed', icon: DollarSign, label: 'Feed' },
    { to: '/pillory', icon: Target, label: 'Pillory' },
];

const devToolItems = [
    { to: '/extensions', icon: Puzzle, label: 'Extensions' },
    { to: '/webhooks', icon: Webhook, label: 'Webhooks' },
    { to: '/developer', icon: Code, label: 'Developer' },
];

const adminMenuItems = [
    { href: '/pulse', icon: Activity, label: 'Pulse' },
    { href: '/telescope', icon: Search, label: 'Telescope' },
    { href: '/horizon', icon: Layers, label: 'Horizon' },
    { href: '/grafana', icon: Bug, label: 'Grafana' },
];

export default function AppLayout() {
    const { user, logout } = useAuth();
    const canActAsDom = user?.role === 'dom' || user?.role === 'switch' || user?.role === 'admin';
    const isAdmin = user?.role === 'admin';
    const navItems = canActAsDom ? domNavItems : baseNavItems;
    const userMenuItems = [
        { to: '/settings', icon: Settings, label: 'Settings' },
        { to: '/contracts', icon: FileText, label: 'Contracts' },
        { to: '/docs', icon: BookOpen, label: 'Docs' },
        ...devToolItems,
        ...(user?.owner ? [{ to: `/profiles/${user.owner.id}`, icon: Crown, label: 'My Owner' }] : []),
    ];
    const location = useLocation();
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const userMenuRef = useRef<HTMLDivElement>(null);

    // Fetch unread notification count
    const { data: unreadData } = useQuery({
        queryKey: ['notifications-unread-count'],
        queryFn: () => api.get<{ data: { id: string }[]; meta: { total: number } }>('/notifications/unread'),
        refetchInterval: 30_000,
    });
    const unreadCount = unreadData?.meta?.total ?? unreadData?.data?.length ?? 0;

    const { data: unreadMessages } = useQuery({
        queryKey: ['unread-count'],
        queryFn: () => api.get<{ unread_count: number }>('/conversations/unread'),
        refetchInterval: 30_000,
    });
    const unreadMessageCount = unreadMessages?.unread_count ?? 0;

    // Close user menu on outside click
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
                setUserMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    // Close user menu on route change
    useEffect(() => {
        setUserMenuOpen(false);
        setMenuOpen(false);
    }, [location.pathname]);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const isActiveRoute = (path: string) =>
        location.pathname === path || location.pathname.startsWith(path + '/');

    const isUserMenuActive = userMenuItems.some((item) => isActiveRoute(item.to)) || isActiveRoute('/notifications');

    return (
        <div className="min-h-screen bg-slate-950 overflow-x-hidden">
            {/* Top bar */}
            <header className="sticky top-0 z-50 border-b border-slate-800 bg-slate-900 pt-safe-top">
                <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-5 sm:px-6">
                    <Link to="/dashboard" className="flex shrink-0 items-center gap-1.5">
                        <img src="/images/monogram.png" alt="DebtBound" className="h-8 w-8" />
                        <span className="text-lg font-bold text-violet-400 hidden sm:inline">DebtBound</span>
                    </Link>

                    {/* Desktop nav */}
                    <nav className="hidden items-center gap-0.5 lg:flex">
                        {navItems.map((item) => (
                            <Link
                                key={item.to}
                                to={item.to}
                                className={`flex items-center gap-1.5 rounded-md px-2.5 py-2 text-sm transition-colors ${
                                    isActiveRoute(item.to)
                                        ? 'bg-violet-900/40 text-violet-300'
                                        : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                                }`}
                            >
                                <item.icon className="h-4 w-4" />
                                <span className="hidden xl:inline">{item.label}</span>
                            </Link>
                        ))}

                        {/* User / Settings dropdown */}
                        <div className="relative ml-1" ref={userMenuRef}>
                            <button
                                onClick={() => setUserMenuOpen(!userMenuOpen)}
                                className={`flex items-center gap-1.5 rounded-md px-2.5 py-2 text-sm transition-colors ${
                                    isUserMenuActive
                                        ? 'bg-violet-900/40 text-violet-300'
                                        : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                                }`}
                            >
                                <div className="relative">
                                    {user?.avatar_path ? (
                                        <img
                                            src={user.avatar_path}
                                            alt=""
                                            className="h-5 w-5 rounded-full object-cover"
                                        />
                                    ) : (
                                        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-violet-900/50 text-[10px] font-bold text-violet-300">
                                            {user?.display_name?.[0]?.toUpperCase() ?? '?'}
                                        </div>
                                    )}
                                    {unreadCount > 0 && (
                                        <span className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-red-500 text-[8px] font-bold text-white">
                                            {unreadCount > 9 ? '9+' : unreadCount}
                                        </span>
                                    )}
                                </div>
                                <ChevronDown className={`h-3 w-3 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {userMenuOpen && (
                                <div className="absolute right-0 top-full mt-1 w-48 rounded-lg border border-slate-700 bg-slate-900 py-1 shadow-xl">
                                    <div className="border-b border-slate-800 px-3 py-2">
                                        <p className="text-sm font-medium text-slate-200 truncate">
                                            {user?.display_name}
                                        </p>
                                        <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                                    </div>
                                    <Link
                                        to="/notifications"
                                        className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                                            isActiveRoute('/notifications')
                                                ? 'bg-violet-900/40 text-violet-300'
                                                : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                                        }`}
                                    >
                                        <Bell className="h-4 w-4" />
                                        Alerts
                                        {unreadCount > 0 && (
                                            <span className="ml-auto rounded-full bg-red-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-red-400">
                                                {unreadCount}
                                            </span>
                                        )}
                                    </Link>
                                    <Link
                                        to="/messages"
                                        className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                                            isActiveRoute('/messages')
                                                ? 'bg-violet-900/40 text-violet-300'
                                                : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                                        }`}
                                    >
                                        <MessageCircle className="h-4 w-4" />
                                        Messages
                                        {unreadMessageCount > 0 && (
                                            <span className="ml-auto rounded-full bg-violet-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-violet-400">
                                                {unreadMessageCount}
                                            </span>
                                        )}
                                    </Link>
                                    {userMenuItems.map((item) => (
                                        <Link
                                            key={item.to}
                                            to={item.to}
                                            className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                                                isActiveRoute(item.to)
                                                    ? 'bg-violet-900/40 text-violet-300'
                                                    : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                                            }`}
                                        >
                                            <item.icon className="h-4 w-4" />
                                            {item.label}
                                        </Link>
                                    ))}
                                    {isAdmin && (
                                        <>
                                            <div className="border-t border-slate-800 mt-1 pt-1">
                                                <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                                                    Admin Tools
                                                </p>
                                                {adminMenuItems.map((item) => (
                                                    <a
                                                        key={item.href}
                                                        href={item.href}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:bg-slate-800 hover:text-slate-200 transition-colors"
                                                    >
                                                        <item.icon className="h-4 w-4" />
                                                        {item.label}
                                                        <ExternalLink className="ml-auto h-3 w-3 text-slate-600" />
                                                    </a>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                    <div className="border-t border-slate-800 mt-1 pt-1">
                                        <button
                                            onClick={handleLogout}
                                            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-slate-800"
                                        >
                                            <LogOut className="h-4 w-4" />
                                            Logout
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </nav>

                    {/* Mobile/tablet menu toggle */}
                    <div className="flex items-center gap-2 lg:hidden">
                        <Link
                            to="/messages"
                            className={`relative rounded-md p-2 text-sm transition-colors ${
                                isActiveRoute('/messages')
                                    ? 'text-violet-300'
                                    : 'text-slate-400 hover:text-slate-200'
                            }`}
                        >
                            <MessageCircle className="h-5 w-5" />
                            {unreadMessageCount > 0 && (
                                <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-violet-500 text-[9px] font-bold text-white">
                                    {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
                                </span>
                            )}
                        </Link>
                        <Link
                            to="/notifications"
                            className={`relative rounded-md p-2 text-sm transition-colors ${
                                isActiveRoute('/notifications')
                                    ? 'text-violet-300'
                                    : 'text-slate-400 hover:text-slate-200'
                            }`}
                        >
                            <Bell className="h-5 w-5" />
                            {unreadCount > 0 && (
                                <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white">
                                    {unreadCount > 9 ? '9+' : unreadCount}
                                </span>
                            )}
                        </Link>
                        <button
                            className="text-slate-300"
                            onClick={() => setMenuOpen(!menuOpen)}
                        >
                            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </button>
                    </div>
                </div>

                {/* Mobile/tablet nav dropdown */}
                {menuOpen && (
                    <nav className="border-t border-slate-800 bg-slate-900 py-2 px-5 sm:px-6 lg:hidden max-h-[70vh] overflow-y-auto">
                        {navItems.map((item) => (
                            <Link
                                key={item.to}
                                to={item.to}
                                className={`flex items-center gap-2 rounded-md px-3 py-2.5 text-sm ${
                                    isActiveRoute(item.to)
                                        ? 'bg-violet-900/40 text-violet-300'
                                        : 'text-slate-400'
                                }`}
                            >
                                <item.icon className="h-4 w-4" />
                                {item.label}
                            </Link>
                        ))}
                        <div className="border-t border-slate-800 my-1" />
                        <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                            Settings
                        </p>
                        {userMenuItems.map((item) => (
                            <Link
                                key={item.to}
                                to={item.to}
                                className={`flex items-center gap-2 rounded-md px-3 py-2.5 text-sm ${
                                    isActiveRoute(item.to)
                                        ? 'bg-violet-900/40 text-violet-300'
                                        : 'text-slate-400'
                                }`}
                            >
                                <item.icon className="h-4 w-4" />
                                {item.label}
                            </Link>
                        ))}
                        {isAdmin && (
                            <>
                                <div className="border-t border-slate-800 my-1" />
                                <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                                    Admin Tools
                                </p>
                                {adminMenuItems.map((item) => (
                                    <a
                                        key={item.href}
                                        href={item.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-slate-400"
                                    >
                                        <item.icon className="h-4 w-4" />
                                        {item.label}
                                        <ExternalLink className="ml-auto h-3 w-3 text-slate-600" />
                                    </a>
                                ))}
                            </>
                        )}
                        <div className="border-t border-slate-800 my-1" />
                        <button
                            onClick={handleLogout}
                            className="flex w-full items-center gap-2 rounded-md px-3 py-2.5 text-sm text-red-400"
                        >
                            <LogOut className="h-4 w-4" />
                            Logout
                        </button>
                    </nav>
                )}
            </header>

            {/* Page content */}
            <main className="mx-auto max-w-5xl px-5 py-6 pb-24 sm:px-6 lg:pb-6">
                <Outlet />
                <div className="mt-8 text-center text-[10px] text-slate-700">
                    <p>v{APP_VERSION}</p>
                    <div className="mt-1 flex justify-center gap-3">
                        <Link to="/docs" className="hover:text-slate-500 transition-colors">Docs</Link>
                        <Link to="/privacy" className="hover:text-slate-500 transition-colors">Privacy</Link>
                        <Link to="/terms" className="hover:text-slate-500 transition-colors">Terms</Link>
                    </div>
                </div>
            </main>

            {/* Mobile/tablet bottom nav */}
            <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-slate-800 bg-slate-900 pb-safe-bottom lg:hidden">
                <div className="flex justify-around py-2">
                    {navItems.slice(0, 5).map((item) => (
                        <Link
                            key={item.to}
                            to={item.to}
                            className={`flex flex-col items-center gap-0.5 px-2 py-1 text-[10px] ${
                                isActiveRoute(item.to)
                                    ? 'text-violet-400'
                                    : 'text-slate-500'
                            }`}
                        >
                            <item.icon className="h-5 w-5" />
                            {item.label}
                        </Link>
                    ))}
                </div>
            </nav>
        </div>
    );
}
